<!-- NOTE: this component is not performant in case of many workpackages, since it requires
preloading items in advance. As the clients' database size grew, this needs to be refactored to use
AssortmentSearch for WP lookup instead. See PeriodsTab.vue for an example. -->
<template>
  <div class="search-scenario w-100">
    <div v-if="block" class="block-container" data-id-e2e="searchScenario">
      <div class="mb-1">
        {{ tkey('scenarioImportDescription') }}
      </div>
      <v-autocomplete
        ref="autocomplete"
        v-model="selectedWorkpackageId"
        v-model:search="wpSearchInput"
        data-id-e2e="searchScenarioWpAutocompleteBlock"
        class="mb-1"
        item-title="name"
        item-value="_id"
        :items="workpackages"
        :custom-filter="filterItems"
        :placeholder="tkey('selectWorkpackage')"
        :menu-props="{ maxWidth: 0 }"
        variant="underlined"
        hide-details
        @update:model-value="onWorkpackageChange"
      />
      <v-row class="ma-0" align="center" justify="space-between">
        <v-autocomplete
          ref="autocomplete"
          v-model="selectedScenarioId"
          v-model:search="scenarioSearchInput"
          data-id-e2e="searchScenarioScenarioAutocompleteBlock"
          item-title="name"
          item-value="_id"
          :items="wpScenarios"
          :custom-filter="filterItems"
          :disabled="!selectedWorkpackageId"
          :placeholder="tkey('selectScenario')"
          :rules="rules"
          :menu-props="{ maxWidth: 0 }"
          variant="underlined"
          clearable
          hide-details
          @click:clear="clearSelections"
          @update:model-value="setSelectedScenarioId"
        />
      </v-row>
    </div>
    <v-row v-if="inline" class="inline-container d-flex align-center justify-space-between">
      <v-col cols="9" class="px-0 py-0">
        <v-row class="d-flex align-center justify-space-between">
          <v-col cols="auto" class="d-flex align-center py-0">
            <span>{{ $t('general.filter') }}</span>
          </v-col>
          <v-col class="d-flex align-center overflow-ellipsis py-1">
            <v-autocomplete
              ref="autocomplete"
              v-model="selectedWorkpackageId"
              v-model:search="wpSearchInput"
              data-id-e2e="searchScenarioWpAutocompleteInline"
              item-title="name"
              item-value="_id"
              :items="workpackages"
              :custom-filter="filterItems"
              :placeholder="tkey('selectWorkpackage')"
              hide-details
              bg-color="white"
              @update:model-value="onWorkpackageChange"
            />
          </v-col>
          <v-col class="d-flex align-center px-0 overflow-ellipsis py-1">
            <v-autocomplete
              ref="autocomplete"
              v-model="selectedScenarioId"
              v-model:search="scenarioSearchInput"
              data-id-e2e="searchScenarioScenarioAutocompleteInline"
              item-title="name"
              item-value="_id"
              :items="wpScenarios"
              :custom-filter="filterItems"
              :disabled="!selectedWorkpackageId"
              :placeholder="tkey('selectScenario')"
              :rules="rules"
              bg-color="white"
              hide-details
              @update:model-value="setSelectedScenarioId"
            />
          </v-col>
          <v-col cols="1" class="d-flex align-center px-1 py-0">
            <v-btn icon variant="text" @click="clearSelections">
              <v-icon size="20" color="primary"> mdi-close-circle </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="3" class="d-flex align-center justify-end py-0">
        <v-btn
          class="ml-2"
          data-id-e2e="searchScenarioViewBtn"
          secondary
          variant="flat"
          :disabled="!selectedScenarioId"
          @click="viewResults"
        >
          {{ tkey('view') }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { filter, isEmpty, get, merge } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useScenariosStore } from '@/stores/scenarios';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useInternationalization } from '@/composables/use-internationalization';
import { useFeature } from '@/composables/use-feature';

export default {
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    block: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    activeScenarioId: {
      type: String,
      required: false,
      default: '',
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    scenariosSearchQuery: {
      type: Object,
      required: false,
      default: () => {},
    },
    useLinkedDefaults: {
      type: Boolean,
      required: false,
      default: () => {
        const { previousWorkpackageLinkingEnabled } = useFeature();
        return previousWorkpackageLinkingEnabled.value;
      },
    },
  },

  setup() {
    const { tkey } = useInternationalization('assortmentSearch');
    return { tkey };
  },

  data() {
    return {
      loading: false,
      search: null,
      hint: '',
      selectedWorkpackageId: null,
      selectedScenarioId: null,
      workpackages: [],
      wpScenarios: [],
      wpSearchInput: null,
      scenarioSearchInput: null,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'activeWorkpackages',
      'selectedWpLinkedWp',
      'selectedWpLinkedScenario',
    ]),
  },

  async created() {
    // Start both promises at the same time as they do not depend on one another.
    await Promise.all([this.fetchWorkpackages(), this.setInitialWorkpackageAndScenario()]);

    // The workpackage dropdown will include all active workpackages and (where available) the linked workpackage,
    // even if that linked workpackage is archived. See discussion on AOV3-7476 for more information.
    // It's important that this.workpackages is set after setInitialWorkpackageAndScenario has completed to avoid a race condition.
    this.workpackages = [
      ...this.activeWorkpackages,
      ...(this.selectedWpLinkedWp ? [this.selectedWpLinkedWp] : []),
    ];
  },

  methods: {
    ...mapActions(useWorkpackagesStore, ['fetchWorkpackages']),
    ...mapActions(useScenariosStore, ['fetchScenarios']),

    filterItems(value, queryText, item) {
      if (isEmpty(queryText)) return true;

      const itemName = get(item, 'title', '');
      const query = queryText.toLocaleLowerCase();
      return itemName.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) > -1;
    },

    async onWorkpackageChange() {
      this.wpScenarios = [];
      this.selectedScenarioId = null;
      this.setSelectedScenarioId(); // reset selected scenario

      await this.refreshWorkpackageScenarios();
    },

    async refreshWorkpackageScenarios() {
      const where = merge(
        {},
        { workpackageId: this.selectedWorkpackageId },
        this.scenariosSearchQuery
      );

      const scenarios = await this.fetchScenarios({
        params: { where, pick: ['_id', 'name'] },
        options: { skipCommit: true },
      });
      this.wpScenarios = filter(scenarios, ({ _id }) => _id !== this.activeScenarioId);
    },

    /**
     * Sets the initial workpackage and scenario dropdown values when provided.
     * When available, the linked workpackage and scenario are added to the dropdowns.
     * This ensures the initial workpackage & scenario are instantly available and selected in cases
     * where the initial workpackage & scenario provided are the same as the linked workpackage & scenario.
     */
    async setInitialWorkpackageAndScenario() {
      if (!this.useLinkedDefaults) return;

      const linkedWp = this.selectedWpLinkedWp;
      const linkedScenario = this.selectedWpLinkedScenario;

      if (!linkedWp) return;

      this.workpackages = [linkedWp];
      this.selectedWorkpackageId = linkedWp._id;
      // It's important that the search input is updated for the emit from viewResults to work.
      this.wpSearchInput = get(linkedWp, 'name');

      // When we've set the workpackage dropdown, we should pull back all of its scenarios.
      const refreshScenariosPromise = this.refreshWorkpackageScenarios();

      if (linkedScenario) {
        // This search query is used to retrieve the list of scenarios for the selected workpackage.
        // If the search query isn't satisfied, the scenario won't be returned in that API call. That means we shouldn't
        // optimistically add it to `this.wpScenarios` and pre-select it because it's not a valid scenario to select.
        const linkedScenarioSatisfiesSearchQuery =
          isEmpty(this.scenariosSearchQuery) ||
          Object.entries(this.scenariosSearchQuery).every(
            ([key, value]) => get(linkedScenario, key) === value
          );

        if (linkedScenarioSatisfiesSearchQuery) {
          this.wpScenarios = [linkedScenario];
          this.selectedScenarioId = linkedScenario._id;

          // It's important that the search input is updated for the emit from viewResults to work.
          this.scenarioSearchInput = get(linkedScenario, 'name');
          this.setSelectedScenarioId();
        }
      }

      await refreshScenariosPromise;
    },

    setSelectedScenarioId() {
      this.$emit('scenario-id-selected', this.selectedScenarioId);
    },

    viewResults() {
      const workpackage = this.workpackages.find(w => w._id === this.selectedWorkpackageId)?.name;
      const scenario = this.wpScenarios.find(s => s._id === this.selectedScenarioId)?.name;
      this.$emit('view-results', `${workpackage} / ${scenario}`);
    },

    clearSelections() {
      this.selectedWorkpackageId = null;
      this.selectedScenarioId = null;
      this.wpSearchInput = null;
      this.scenarioSearchInput = null;
      this.setSelectedScenarioId();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.search-scenario {
  .inline-container {
    border-top: $assortment-primary-colour 1px solid;
    background-color: $assortment-filter-bar-bg-colour;
  }
}

:deep(.v-input__control) {
  .v-select__slot {
    margin-left: 5px;
  }
}
</style>
